import { useState } from 'react';
import { toast } from 'sonner';

import { notifyTagSaved, useCreateTagMutation } from '~entities/tags';
import { useGetTags } from '~entities/tags/api/get-groups';

import Creatable from '~shared/components/select/creatable';
import { capitalizeWords } from '~shared/helpers/text-formatting';

export interface TagsOption {
  label: string;
  value: string;
}

export interface TagsSelectorProps {
  value: Array<TagsOption>;
  onChange: (value: Array<TagsOption> | undefined | null) => void;
  isOptionDisabled?: (option: TagsOption) => boolean;
  isDisabled?: boolean;
  isMulti?: boolean;
  placeholder?: string;
  /**
   * Portal target for the menu. Inside Radix dialogs pass `null` (and
   * `overflow-visible` on DialogContent) so option clicks are not blocked by
   * body `pointer-events: none`. Omit to keep the default body portal.
   */
  menuPortalTarget?: HTMLElement | null;
}
export const TagsSelector = ({
  value,
  onChange,
  isOptionDisabled,
  isDisabled,
  isMulti = true,
  placeholder = 'Select or type a new tag',
  menuPortalTarget,
}: TagsSelectorProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  // const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const mutation = useCreateTagMutation();

  const { data, isFetching } = useGetTags({
    page: 1,
    limit: 10,
    search: searchQuery,
  });

  const options = data?.items.map((tag) => ({
    label: capitalizeWords(tag.fieldName),
    value: String(tag._id),
  }));

  return (
    <Creatable
      value={isMulti ? value : (value[0] ?? null)}
      onChange={(option) => {
        if (isMulti) {
          const opt = option as Array<TagsOption> | null | undefined;
          onChange(
            opt?.map((item) => ({
              label: item.label,
              value: String(item.value),
            })),
          );
          return;
        }
        const opt = option as TagsOption | null | undefined;
        onChange(
          opt
            ? [{ label: opt.label, value: String(opt.value) }]
            : [],
        );
      }}
      onCreateOption={async (inputValue: string) => {
        try {
          const tag = await mutation.mutateAsync({ fieldName: inputValue });
          const created = {
            label: capitalizeWords(tag.fieldName),
            value: String(tag._id),
          };
          onChange(isMulti ? [...value, created] : [created]);
          notifyTagSaved('Tag created successfully', tag);
        } catch (error) {
          console.error(error);
          toast.error('Failed to create tag. Try selecting an existing tag.');
        }
      }}
      onInputChange={(inputValue) => {
        setSearchQuery(inputValue);
      }}
      isOptionDisabled={(option) => {
        if (!isOptionDisabled) return false;
        return isOptionDisabled(option as TagsOption);
      }}
      isDisabled={isDisabled}
      isMulti={isMulti}
      placeholder={placeholder}
      options={options}
      isLoading={isFetching || mutation.isPending}
      menuPortalTarget={menuPortalTarget}
    />
  );
};
